import { useEffect, useMemo, useState } from 'react'
import { stringAvatar } from '@yuno/design-system-web'
import { AccountProp, FIRST_TAB } from '../account-menu.styled'
import { useAccount } from '../../../hooks/use-account'
import { storage, Item } from '../../../lib/storage'
import { Organization } from '@yuno/types'
import { queryClient } from '@yuno/dashboard-api-mfe'
import { useSnackbarContext } from '../../../yuno-dashboard-container-mfe'
import { useTranslation } from 'react-i18next'

const findSelectedAccount = (accounts: AccountProp[]) => {
  const accountStored = storage.getItem<Organization.Account>(Item.ACCOUNT)

  if (accountStored !== null && accountStored !== undefined) {
    return accounts.find((account) => account.name === accountStored.name)
  }

  return accounts.find((account) => account.principal) || accounts[0]
}

export const useAccountMenu = ({ accounts, handleSection }) => {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(FIRST_TAB)
  const account = useAccount()
  const [selectedAccount, setSelectedAccount] = useState<AccountProp>(
    findSelectedAccount(accounts),
  )
  const { onOpenSnackbar } = useSnackbarContext()
  const open = Boolean(anchorEl)

  useEffect(() => {
    if (accounts.length > 0) {
      const selected = findSelectedAccount(accounts)

      setSelectedAccount(selected)
    }
  }, [accounts])

  useEffect(() => {
    if (account) {
      setSelectedAccount(account)
    }
  }, [account])

  const accountSplit = useMemo(() => {
    if (!selectedAccount) {
      return ''
    }

    return stringAvatar(selectedAccount.name)
  }, [selectedAccount])

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setSelectedTabIndex(0)
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }

  const onSelectAccount = async (account: AccountProp) => {
    if (account.code !== selectedAccount.code) {
      try {
        storage.setItem(Item.ACCOUNT, account)
        onOpenSnackbar(
          t('account_menu.accountChanged'),
          'success',
          'CheckCircle',
        )

        localStorage.removeItem('paymentsSearchParams')
        localStorage.removeItem('transactionsSearchParams')
        localStorage.removeItem('insightsFilters')

        queryClient.removeQueries({})
        queryClient.resetQueries({})

        const clearFiltersEvent = new Event('CLEAR_FILTER')
        window.dispatchEvent(clearFiltersEvent)

        handleClose()
      } catch {
        onOpenSnackbar(t('account_menu.accountNotChanged'), 'error', 'Info')
      }
    }
  }

  const handleNav = (path: string) => () => {
    handleSection(null)
    handleClose()
    ;(window as any).singleSpaNavigate(path)
  }

  return {
    open,
    selectedTabIndex,
    accountSplit,
    handleClick,
    handleClose,
    onSelectAccount,
    selectedAccount,
    anchorEl,
    setSelectedTabIndex,
    handleNav,
  }
}
